import { useId, useMemo, useState } from 'react';
import type React from 'react';
import {
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxValue,
} from '@/components/ui/combobox';
import { Label } from '@/components/ui/label';

export type ComboboxFieldOption = {
  value: string;
  label: string;
};

type ComboboxFieldProps = {
  options: ComboboxFieldOption[];
  multiple?: boolean;
  label?: string;
  placeholder?: string;
  emptyMessage?: string;
  disabled?: boolean;
  creatable?: boolean;
  onCreateOption?: (value: string) => void;
  value?: string | string[] | null;
  defaultValue?: string | string[] | null;
  onChange?: (value: string | string[] | null) => void;
};

export const ComboboxField: React.FC<ComboboxFieldProps> = ({
  options,
  multiple = false,
  label,
  placeholder = 'Select...',
  emptyMessage = 'No items found.',
  disabled,
  creatable = false,
  onCreateOption,
  value: valueProp,
  defaultValue,
  onChange,
}) => {
  const inputId = useId();
  const [query, setQuery] = useState('');
  const [createdOptions, setCreatedOptions] = useState<ComboboxFieldOption[]>(
    []
  );
  const [internalValue, setInternalValue] = useState<string | string[] | null>(
    defaultValue ?? (multiple ? [] : null)
  );
  const isControlled = valueProp !== undefined;
  const value = isControlled ? valueProp : internalValue;

  const availableOptions = useMemo(() => {
    if (!creatable) return options;
    const combined = [...options];
    for (const option of createdOptions) {
      if (!combined.some((existing) => existing.value === option.value)) {
        combined.push(option);
      }
    }
    const values = Array.isArray(value) ? value : value == null ? [] : [value];
    for (const selectedValue of values) {
      if (
        selectedValue.trim() &&
        !combined.some((option) => option.value === selectedValue)
      ) {
        combined.push({ value: selectedValue, label: selectedValue });
      }
    }
    return combined;
  }, [options, createdOptions, creatable, value]);

  // Explicit string comparison preserves punctuation (unlike Intl.Collator).
  const normalize = (text: string) =>
    text.trim().normalize('NFKC').toLowerCase();
  const matches = (option: ComboboxFieldOption, text: string) =>
    normalize(option.label).includes(normalize(text)) ||
    normalize(option.value).includes(normalize(text));
  const trimmedQuery = query.trim();
  const createOption =
    creatable &&
    trimmedQuery &&
    !availableOptions.some((option) => matches(option, trimmedQuery))
      ? { value: trimmedQuery, label: trimmedQuery }
      : null;
  const items = createOption
    ? [...availableOptions, createOption]
    : availableOptions;

  const selectedOptions = useMemo(() => {
    if (multiple) {
      const values = Array.isArray(value) ? value : [];
      return availableOptions.filter((option) => values.includes(option.value));
    } else if (value === null) {
      return null;
    } else {
      return availableOptions.find((option) => option.value === value) ?? null;
    }
  }, [availableOptions, value, multiple]);

  return (
    <div className="tw:flex tw:flex-col tw:gap-2">
      {label && <Label htmlFor={inputId}>{label}</Label>}
      <Combobox
        items={items}
        onInputValueChange={creatable ? setQuery : undefined}
        filter={
          creatable
            ? (option, text) => option === createOption || matches(option, text)
            : undefined
        }
        value={selectedOptions}
        multiple={multiple}
        onValueChange={(nextValue) => {
          const nextOptions = Array.isArray(nextValue)
            ? nextValue
            : nextValue
              ? [nextValue]
              : [];
          if (createOption && nextOptions.includes(createOption)) {
            setCreatedOptions((previous) => [...previous, createOption]);
            onCreateOption?.(createOption.value);
          }
          if (multiple) {
            const nextOptions = Array.isArray(nextValue) ? nextValue : [];
            const nextValues = nextOptions.map((option) => option.value);
            if (!isControlled) setInternalValue(nextValues);
            onChange?.(nextValues);
            return;
          }

          const option = nextValue as ComboboxFieldOption | null;
          const nextValueString = option?.value ?? null;
          if (!isControlled) setInternalValue(nextValueString);
          onChange?.(nextValueString);
        }}
        itemToStringLabel={(option) => option.label}
        itemToStringValue={(option) => option.value}
        isItemEqualToValue={(a, b) => a.value === b.value}
        disabled={disabled}
      >
        {multiple ? (
          <ComboboxChips>
            <ComboboxValue>
              {(selectedOptions as ComboboxFieldOption[]).map((option) => (
                <ComboboxChip key={option.value}>{option.label}</ComboboxChip>
              ))}
            </ComboboxValue>
            <ComboboxChipsInput
              id={inputId}
              placeholder={placeholder}
              disabled={disabled}
            />
          </ComboboxChips>
        ) : (
          <ComboboxInput
            id={inputId}
            placeholder={placeholder}
            disabled={disabled}
            showClear
          />
        )}
        <ComboboxContent>
          <ComboboxEmpty>{emptyMessage}</ComboboxEmpty>
          <ComboboxList>
            {(option) => (
              <ComboboxItem key={option.value} value={option}>
                {option === createOption
                  ? `Create "${option.label}"`
                  : option.label}
              </ComboboxItem>
            )}
          </ComboboxList>
        </ComboboxContent>
      </Combobox>
    </div>
  );
};
